import { LAUNCH_CRITICAL_EVENT_STEP_UUID } from './launchCriticalEvent';
import { CONDITION_STEP_UUID } from './conditionStep';

export const ScriptStepStageDefaultOrder = {
  DEPLOYED: 1,
  DEVELOPMENT: 2,
  DEPRECATED: 3,
  WITHDRAWN: 4,
};

export const ScriptStepStageVersionListOrder = {
  DEVELOPMENT: 1,
  DEPLOYED: 2,
  DEPRECATED: 3,
  WITHDRAWN: 4,
};

export const ScriptStepStageToMessageId = {
  DEVELOPMENT: 'STEP_STAGE_DEVELOPMENT',
  DEPLOYED: 'STEP_STAGE_DEPLOYED',
  WITHDRAWN: 'STEP_STAGE_WITHDRAWN',
  DEPRECATED: 'STEP_STAGE_DEPRECATED',
};

export const ScriptStepStageDefs = {
  DEVELOPMENT: 'DEVELOPMENT',
  DEPLOYED: 'DEPLOYED',
  WITHDRAWN: 'WITHDRAWN',
  DEPRECATED: 'DEPRECATED',
};

export const ExcludeFromPalette = [
  ScriptStepStageDefs.WITHDRAWN,
  ScriptStepStageDefs.DEPRECATED,
];

export const scriptStepTypes = {
  AUTOMATION_TRIGGER: 'AUTOMATION_TRIGGER',
  BUILT_IN: 'BUILT_IN',
  BUILT_IN_HTTP: 'BUILT_IN_HTTP',
  BUILT_IN_INTERNAL: 'BUILT_IN_INTERNAL',
  CALLABLE_FLOW_CALLER: 'CALLABLE_FLOW_CALLER',
  CALLABLE_FLOW_RECEIVER: 'CALLABLE_FLOW_RECEIVER',
  CREATE_ALERT: 'CREATE_ALERT',
  CREATE_EVENT: 'CREATE_EVENT',
  CUSTOM_HTTP: 'CUSTOM_HTTP',
  FORM_TRIGGER: 'FORM_TRIGGER',
  INBOUND_EMAIL: 'INBOUND_EMAIL',
  INBOUND_EMAIL_UTILITIES: 'INBOUND_EMAIL_UTILITIES',
  INBOUND_WEBHOOK: 'INBOUND_WEBHOOK',
  INCIDENT_MANAGEMENT: 'INCIDENT_MANAGEMENT',
  LEGACY_INBOUND: 'LEGACY_INBOUND',
  LEGACY_OUTBOUND: 'LEGACY_OUTBOUND',
  MERGE: 'MERGE',
  OUTBOUND_WEBHOOK: 'OUTBOUND_WEBHOOK',
  PLAN: 'PLAN',
  RESPONSES: 'RESPONSES',
  SCRIPT_STEP: 'SCRIPT_STEP',
  SWITCH: 'SWITCH',
  USER: 'USER',
  UTILITY: 'UTILITY',
  XMATTERS: 'XMATTERS',
  INITIATE_INCIDENT: 'INITIATE_INCIDENT',
  EBFD_INCIDENT_RESPONSE_TRIGGER: 'EBFD_INCIDENT_RESPONSE_TRIGGER',
  TEMPLATE_EBFD_LAUNCH_INCIDENT_STEP: 'TEMPLATE_EBFD_LAUNCH_INCIDENT_STEP',
  EBFD_LAUNCH_INCIDENT_STEP: 'EBFD_LAUNCH_INCIDENT_STEP',
  TEMPLATE_EBFD_UPDATE_INCIDENT_STEP: 'TEMPLATE_EBFD_UPDATE_INCIDENT_STEP',
  EBFD_UPDATE_INCIDENT_STEP: 'EBFD_UPDATE_INCIDENT_STEP',
  EBFD_CLOSE_INCIDENT_STEP_V2: 'EBFD_CLOSE_INCIDENT_STEP_V2',
  TEMPLATE_EBFD_CLOSE_INCIDENT_STEP_V2: 'TEMPLATE_EBFD_CLOSE_INCIDENT_STEP_V2',
};

export const scriptStepTypesThatCantBeDuplicated = new Set([
  scriptStepTypes.OUTBOUND_WEBHOOK,
  scriptStepTypes.INBOUND_WEBHOOK,
  scriptStepTypes.LEGACY_OUTBOUND,
  scriptStepTypes.LEGACY_INBOUND,
  scriptStepTypes.FORM_TRIGGER,
  scriptStepTypes.AUTOMATION_TRIGGER,
  scriptStepTypes.CALLABLE_FLOW_RECEIVER,
  scriptStepTypes.RESPONSES,
  scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER,
  scriptStepTypes.EBFD_LAUNCH_INCIDENT_STEP,
  scriptStepTypes.TEMPLATE_EBFD_LAUNCH_INCIDENT_STEP,
  scriptStepTypes.EBFD_UPDATE_INCIDENT_STEP,
  scriptStepTypes.TEMPLATE_EBFD_UPDATE_INCIDENT_STEP,
  scriptStepTypes.EBFD_CLOSE_INCIDENT_STEP_V2,
  scriptStepTypes.TEMPLATE_EBFD_CLOSE_INCIDENT_STEP_V2,
]);

export const scriptStepUUIDThatCantBeDuplicated = new Set([
  LAUNCH_CRITICAL_EVENT_STEP_UUID,
  CONDITION_STEP_UUID,
]);

export const outboundScriptStepTypes = new Set([
  scriptStepTypes.OUTBOUND_WEBHOOK,
  scriptStepTypes.LEGACY_OUTBOUND,
]);

export const builtInTypes = {
  WEB_HOOK: 'WEB_HOOK',
  XMATTERS_CREATE_INCIDENT: 'XMATTERS_CREATE_INCIDENT',
  XMATTERS_NOTIFY: 'XMATTERS_NOTIFY',
  XMATTERS_EVENTS: 'XMATTERS_EVENTS',
  CONDITION: 'BUILT_IN_CONDITION',
};

export const uniquelyLabeledStepTypes = new Set([
  scriptStepTypes.CREATE_ALERT,
  scriptStepTypes.SCRIPT_STEP,
  builtInTypes.XMATTERS_CREATE_INCIDENT,
]);

export const incidentTriggerBuiltInTypes = {
  SEVERITY_CHANGE: 'XMATTERS_INCIDENT_SEVERITY_CHANGE',
  STATUS_CHANGE: 'XMATTERS_INCIDENT_STATUS_CHANGE',
  NOTIFY_TO_ENGAGE: 'XMATTERS_INCIDENT_NOTIFY_TO_ENGAGE',
};

export const customScriptStepTypes = new Set([
  scriptStepTypes.PLAN,
  scriptStepTypes.USER,
  scriptStepTypes.CUSTOM_HTTP,
  scriptStepTypes.CALLABLE_FLOW_CALLER,
]);

export const customEntityTypes = new Set([
  scriptStepTypes.CUSTOM_HTTP,
  'SCRIPT_STEP',
  'LIBRARY',
]);

export const versionEnabledScriptStepTypes = new Set([
  scriptStepTypes.BUILT_IN,
  scriptStepTypes.BUILT_IN_HTTP,
  scriptStepTypes.CUSTOM_HTTP,
  scriptStepTypes.USER,
]);

export function sortForDefault(a, b) {
  if (a.stage === b.stage) {
    return b.version - a.version;
  }

  return (
    ScriptStepStageDefaultOrder[a.stage] - ScriptStepStageDefaultOrder[b.stage]
  );
}

export function sortForVersionList(a, b) {
  if (a.stage === b.stage) {
    return b.version - a.version;
  }

  return (
    ScriptStepStageVersionListOrder[a.stage] -
    ScriptStepStageVersionListOrder[b.stage]
  );
}

export const StepListFilterType = {
  OWNED: 'OWNED',
  SHARED: 'SHARED',
  ALL: 'ALL',
};

// Some headers intentionally missing to keep single source of truth (e.g. "SLACK": "Slack" in en.json):
export const headerTranslationKey = {
  [scriptStepTypes.BUILT_IN_HTTP]: 'FLOW_DESIGN_PALETTE_HEADER_BUILT_IN_HTTP',
  [scriptStepTypes.CUSTOM_HTTP]: 'FLOW_DESIGN_PALETTE_HEADER_CUSTOM_HTTP',
  [scriptStepTypes.OUTBOUND_WEBHOOK]:
    'FLOW_DESIGN_PALETTE_HEADER_OUTBOUND_WEBHOOK',
  [scriptStepTypes.INCIDENT_MANAGEMENT]: 'INCIDENT',
  EVENTS: 'FLOW_DESIGN_PALETTE_HEADER_ALERTS',
  UTILITIES: 'FLOW_DESIGN_PALETTE_HEADER_UTILITIES',
  [scriptStepTypes.INBOUND_EMAIL]: 'FLOW_DESIGN_PALETTE_HEADER_INBOUND_EMAIL',
  [scriptStepTypes.INBOUND_EMAIL_UTILITIES]:
    'FLOW_DESIGN_PALETTE_HEADER_UTILITIES',
  FIND: 'FLOW_DESIGN_PALETTE_HEADER_FIND_IN_XMATTERS',
  [scriptStepTypes.FORM_TRIGGER]: 'FLOW_DESIGN_PALETTE_HEADER_FORM_TRIGGER',
  TRIGGER_UTILITY: 'FLOW_DESIGN_PALETTE_HEADER_UTILITY_TRIGGERS',
  CALLABLE_FLOW_CALLER: 'FLOW_DESIGN_PALETTE_HEADER_CALLABLE_FLOWS',
  CALLABLE_FLOW_RECEIVER: 'FLOW_DESIGN_PALETTE_HEADER_CALLABLE_FLOWS',
  [scriptStepTypes.INITIATE_INCIDENT]: 'INITIATE_INCIDENT',
  [scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER]:
    'FLOW_DESIGN_PALETTE_HEADER_BUILT_IN_HTTP',
};

export function getHeaderTranslationKey(stepType) {
  if (!stepType) {
    return stepType;
  }

  const mappedKey = headerTranslationKey[stepType];
  return mappedKey;
}

export const defaultStepIconPath =
  '/static/images/script_steps/default-brackets.svg';

export const defaultTriggerIconPath =
  '/static/images/script_steps/http-trigger.svg';

export const defaultCallableTriggerIconPath =
  '/static/images/script_steps/callable-flow.svg';

export const MINIMUM_INPUT_MAX_LENGTH = 1;
export const INPUT_MAX_LENGTH = 20000;
export const OUTPUT_MAX_LENGTH = 100;
export const NAME_MAX_LENGTH = 120;
export const OUTPUT_DESCRIPTION_MAX_LENGTH = 300;

export const isFormBasedTrigger = type =>
  [
    scriptStepTypes.FORM_TRIGGER,
    scriptStepTypes.AUTOMATION_TRIGGER,
    'TRIGGER_INCIDENT',
  ].includes(type);

export const FORM_MODE = {
  CREATE: 'CREATE',
  SELECT: 'SELECT',
};

// dynamicConfigEnabled steps are any step (trigger or otherwise)
// that require a config-time endpoint as opposed to run-time endpoint.
// Config-time endpoints allow on-the-fly definition of dynamic outputs
// (and dynamic inputs eventually)
// to be "named" with data coming from a 3rd party system based on user selection.
// A flowBlock dataOriginRoot represents an entity in a 3rd party platform
// from which the data used to populate dynamicOutputs comes from.
// For example a "Table" in ServiceNow
// The shape of a dataOriginRoot is most likely going to be that of a dropdown option.
// The input name this constant holds is the name that must be used in a script step input definition
// for the input that will contain the dataOriginRoot
// and that the step's script code may need to access at execution time.
// All dynamically configurable script step should have this input (and use this name).
// (This is closely related to "Data Origin Pointer" which is unique to a given dynamic output)
// In the context of dynamic outputs and dynamic inputs,
// we can't know the name of the inputs/outputs as they are set by the user,
// but the underlying "readOnly" inputs that make these things work are "known"
// these are their names:
export const DATA_ORIGIN_ROOT = 'Data Origin Root';
export const OUTPUT_MAPPINGS = 'Output Mappings'; // This is an INPUT name
export const INPUT_MAPPINGS = 'Input Mappings'; // This is also an INPUT name

export const ADD_TASK_DUE_TYPE_UUID = 'c51554f2-a200-5b6f-983e-4b05b1688478';
